import * as React from 'react';
import { useState } from 'react';

import { Box, Button, ColumnLayout, FormField, Modal, Multiselect, Textarea } from '@amzn/awsui-components-react-v3';
import * as _ from 'lodash';
import { editGroup } from 'src/api/permissions';
import * as validate from 'src/commons/validationUtils';
import { getOmniOncallLink, getRequestSubmittedWithAutoApproval } from 'src/commons/common';

export interface AddGroupMembersModalProps {
  visible: boolean;
  setVisible: any;
  setNotifications: any;
  activeGroupInfo: any;
  groupId: string;
  groupMembers: any;
  roleOptions: any;
}

export const AddGroupMembersModal = (props: AddGroupMembersModalProps) => {
  const [membersToAdd, setMembersToAdd] = useState([]);
  const [selectedRoleOptions, setSelectedRoleOptions] = useState(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');
  const [validation, setValidation] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');

  const submitAddMembersRequest = async () => {
    // Edit roleMapping will perform a full replace in the backend, therefore, deep copy existing and append new role members.
    const roleMapping = _.cloneDeep(props.activeGroupInfo.roleMapping);
    for (let option of selectedRoleOptions) {
      const roleId = option.value;
      if (!(roleId in roleMapping)) {
        roleMapping[roleId] = { roleMembers: [] };
      }
      roleMapping[roleId].roleMembers = roleMapping[roleId].roleMembers ? roleMapping[roleId].roleMembers : [];
      roleMapping[roleId].roleMembers.push(...membersToAdd);
    }
    await editGroup({
      groupId: props.groupId,
      roleMapping,
    });
  };

  const usersStringToArray = (textAreaString) => {
    const usersArray = textAreaString.split('\n').filter((user) => user != '');
    setMembersToAdd(Array.from(new Set(usersArray)));
    setValidation(false);
    setInputValue(textAreaString);
  };

  const validateAddIamPrincipalsRequest = () => {
    if (membersToAdd.length === 0) {
      props.setNotifications('Invalid IAM principal found', 'At least one IAM principal is required');
      setValidation(true);
      setErrorText('New members must not be empty');
      return false;
    }

    const invalidIamPrincipals = membersToAdd.filter((principal) => !validate.isValidIAMPrincipal(principal));
    if (invalidIamPrincipals.length > 0) {
      props.setNotifications('Invalid IAM principal found', invalidIamPrincipals.join(' : '));
      setErrorText('Invalid IAM principal found');
      setValidation(true);
      return false;
    }

    let groupMembersId = props.groupMembers.map((memberInfo) => memberInfo.userId);
    const existingUserIds = membersToAdd.filter((userId) => groupMembersId.includes(userId));
    if (existingUserIds.length > 0) {
      props.setNotifications('Existing group member found', existingUserIds.join(' : '));
      setErrorText('Existing group member found');
      setValidation(true);
      return false;
    }

    if (!selectedRoleOptions || !selectedRoleOptions.length) {
      props.setNotifications('Invalid role found', 'At least one role is required');
      return false;
    }

    return true;
  };

  const handleAddIAMPrincipals = async () => {
    setLoading(true);

    if (!validateAddIamPrincipalsRequest()) {
      setLoading(false);
      props.setVisible(false);
      return;
    }

    try {
      await submitAddMembersRequest();
      props.setNotifications('', getRequestSubmittedWithAutoApproval());
    } catch (e) {
      let errorMessage = <>Please contact {getOmniOncallLink()} to help you with your request.</>;
      props.setNotifications('Failed to submit your request.', errorMessage);
      console.error(e);
    }
    props.setVisible(false);
    setLoading(false);
  };

  return (
    <Modal
      onDismiss={() => {
        props.setVisible(false);
      }}
      visible={props.visible}
      size='medium'
      footer={
        <Box float='right'>
          <Button
            variant='link'
            onClick={() => {
              props.setVisible(false);
            }}
          >
            Cancel
          </Button>
          <Button variant='normal' onClick={handleAddIAMPrincipals} loading={loading}>
            Submit request
          </Button>
        </Box>
      }
      header='Add IAM principals'
    >
      <ColumnLayout>
        <FormField
          label='New members'
          description='You can only add IAM users and roles here. For Midway users, please add to your POSIX/Teams under grouping ID.'
          errorText={validation ? errorText : false}
        >
          <Textarea
            onChange={(e) => usersStringToArray(e.detail.value)}
            value={inputValue}
            placeholder='123456789012:role:Admin'
          />
        </FormField>
        <FormField
          label='Role'
          description='Select role(s) that will be granted to the new members.'
          errorText={
            selectedRoleOptions != undefined && !selectedRoleOptions.length ? 'At least one role is required.' : ''
          }
        >
          <Multiselect
            options={props.roleOptions}
            placeholder='Choose options'
            selectedAriaLabel='Selected'
            selectedOptions={selectedRoleOptions}
            onChange={({ detail }) => setSelectedRoleOptions(detail.selectedOptions)}
          />
        </FormField>
      </ColumnLayout>
    </Modal>
  );
};
